import React, { useState } from "react";
import { fetchWithAuth, useUser } from "../Context/UserContext";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import API_URL from "../config";
import { Plus, Pencil } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const inputClass =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50";

const UserEducation = ({ education = [], isOwner, updateProfileEducation }) => {
  const { setUser } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // education será el array que contiene todas las experiencias educativas
  const [educationList, setEducationList] = useState(
    Array.isArray(education) ? education : []
  );

  // educationData será para manejar la educación que se está editando o agregando
  const [educationData, setEducationData] = useState({
    id_education: null,
    institucion_educativa: "",
    titulo_puesto: "",
    disciplina_academica: "",
    fecha_inicio: "",
    fecha_fin: "",
    descripcion: "",
    actualmente: false,
  });

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0]; // Formato YYYY-MM-DD
  };

  const handleSave = async () => {
    console.log(
      "📤 Datos enviados al backend:",
      JSON.stringify({ educationData }, null, 2)
    );

    if (!isOwner) {
      console.error("No tienes permiso para modificar este perfil.");
      return;
    }

    // Validar que los campos obligatorios no estén vacíos
    if (!educationData.institucion_educativa.trim()) {
      toast.error("El campo 'Institución educativa' es obligatorio.");
      return;
    }

    if (!educationData.titulo_puesto.trim()) {
      toast.error("El campo 'Título obtenido' es obligatorio.");
      return;
    }

    if (!educationData.disciplina_academica.trim()) {
      toast.error("El campo 'Disciplina académica' es obligatorio.");
      return;
    }

    if (!educationData.fecha_inicio) {
      toast.error("El campo 'Fecha de inicio' es obligatorio.");
      return;
    }

    if (!educationData.en_curso && !educationData.fecha_fin) {
      toast.warning(
        "Debe ingresar una fecha de finalización o marcar 'Actualmente cursando'."
      );
      return;
    }

    if (
      !educationData.en_curso &&
      new Date(educationData.fecha_fin) < new Date(educationData.fecha_inicio)
    ) {
      toast.warning(
        "La 'Fecha de finalización' debe ser posterior a la 'Fecha de inicio'."
      );
      return;
    }

    try {
      const url = `${API_URL}/profile/save_education`;

      const response = await fetchWithAuth(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ educationData }), // 🔹 Asegurar que va como objeto con "educationData"
      });

      const data = await response.json();
      if (response.ok) {
        setUser((prevUser) => ({
          ...prevUser,
          education: educationData.id_education
            ? prevUser.education.map((edu) =>
                edu.id_education === educationData.id_education
                  ? { ...edu, ...educationData }
                  : edu
              )
            : [educationData, ...prevUser.education],
        }));

        setEducationList((prevList) => {
          return educationData.id_education
            ? prevList.map((edu) =>
                edu.id_education === educationData.id_education
                  ? { ...edu, ...educationData }
                  : edu
              )
            : [educationData, ...prevList];
        });

        setIsModalOpen(false);
      } else {
        console.error("Error al guardar la educación:", data.error);
      }
    } catch (error) {
      console.error("Error en la solicitud:", error);
    }
  };

  const handleChange = (e) => {
    setEducationData({ ...educationData, [e.target.name]: e.target.value });
  };

  const handleEdit = (edu) => {
    setEducationData({
      id_education: edu.id_education, // 🔹 Asegurar que mantiene el ID
      institucion_educativa: edu.institucion_educativa,
      titulo_puesto: edu.titulo_puesto,
      disciplina_academica: edu.disciplina_academica,
      fecha_inicio: formatDate(edu.fecha_inicio),
      fecha_fin: edu.en_curso
        ? new Date().toISOString().split("T")[0]
        : formatDate(edu.fecha_fin),
      descripcion: edu.descripcion,
      actualmente: edu.en_curso,
      en_curso: edu.en_curso,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id_education) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/profile/delete_education/${id_education}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      if (response.ok) {
        setUser((prevUser) => ({
          ...prevUser,
          education: prevUser.education.filter(
            (edu) => edu.id_education !== id_education
          ),
        }));
        setEducationList((prevList) =>
          prevList.filter((edu) => edu.id_education !== id_education)
        );
        setIsModalOpen(false);
      } else {
        console.error("Error al eliminar la educación.");
      }
    } catch (error) {
      console.error("Error en la solicitud:", error);
    }
  };

  const toggleActualmente = () => {
    setEducationData((prevData) => ({
      ...prevData,
      actualmente: !prevData.actualmente,
      en_curso: !prevData.actualmente,
      fecha_fin: !prevData.actualmente ? "" : prevData.fecha_fin,
    }));
  };

  const formatDateWithDuration = (startDate, endDate, isCurrent) => {
    if (!startDate) return "";

    const start = new Date(startDate);
    const today = new Date();
    const end = isCurrent || endDate === null ? today : new Date(endDate);

    const startFormatted = format(start, "MMM. yyyy", { locale: es });
    const endFormatted =
      isCurrent || endDate === null
        ? "Actualidad"
        : format(end, "MMM. yyyy", { locale: es });

    const diffInMonths =
      end.getMonth() -
      start.getMonth() +
      12 * (end.getFullYear() - start.getFullYear());

    const years = Math.floor(diffInMonths / 12);
    const months = diffInMonths % 12;

    const duration =
      years > 0 && months > 0
        ? `${years} año${years !== 1 ? "s" : ""} y ${months} mes${
            months !== 1 ? "es" : ""
          }`
        : years > 0
        ? `${years} año${years !== 1 ? "s" : ""}`
        : `${months} mes${months !== 1 ? "es" : ""}`;

    return `${startFormatted} - ${endFormatted} · ${duration}`;
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">Educación:</h3>
          {isOwner && (
            <button
              onClick={() => {
                setEducationData({
                  id_education: null,
                  institucion_educativa: "",
                  titulo_puesto: "",
                  disciplina_academica: "",
                  fecha_inicio: "",
                  fecha_fin: "",
                  descripcion: "",
                  actualmente: false,
                });
                setIsModalOpen(true);
              }}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Añadir educación"
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>

        {educationList.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            {isOwner
              ? "Registra estudios completados haciendo click al signo '+' que sale en la esquina derecha de esta sección."
              : "El usuario no ha registrado educación todavía."}
          </p>
        ) : (
          <div className="mt-3 space-y-4">
            {educationList.map((edu, index) => (
              <div key={index} className="border-t border-border pt-3 first:border-t-0 first:pt-0">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-foreground">
                    {edu.titulo_puesto} - {edu.institucion_educativa}
                  </h4>
                  {isOwner && (
                    <button
                      onClick={() => handleEdit(edu)}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatDateWithDuration(edu.fecha_inicio, edu.fecha_fin, edu.actualmente)}
                </p>
                <p
                  className="mt-1 text-sm text-muted-foreground"
                  dangerouslySetInnerHTML={{
                    __html: (edu.descripcion || "").replace(/\n/g, "<br>"),
                  }}
                />
              </div>
            ))}
          </div>
        )}

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {educationData.id_education ? "Editar Educación" : "Agregar Educación"}
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground">* El asterisco indica que es obligatorio</p>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  Institución educativa *
                </label>
                <Input
                  type="text"
                  name="institucion_educativa"
                  value={educationData.institucion_educativa}
                  onChange={handleChange}
                  placeholder="Ejemplo: Universidad de Lima"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Título obtenido *</label>
                <Input
                  type="text"
                  name="titulo_puesto"
                  value={educationData.titulo_puesto}
                  onChange={handleChange}
                  placeholder="Ejemplo: Bachiller"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  Disciplina académica*
                </label>
                <Input
                  type="text"
                  name="disciplina_academica"
                  value={educationData.disciplina_academica}
                  onChange={handleChange}
                  placeholder="Ejemplo: Ciencias de la Computación"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Fecha de inicio *</label>
                <Input
                  type="date"
                  name="fecha_inicio"
                  value={educationData.fecha_inicio}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">Fecha de finalización*</label>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    Actualmente cursando:
                    <input
                      type="checkbox"
                      checked={educationData.actualmente}
                      onChange={toggleActualmente}
                    />
                  </label>
                </div>
                <Input
                  type="date"
                  name="fecha_fin"
                  value={educationData.fecha_fin}
                  onChange={handleChange}
                  disabled={educationData.actualmente}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Descripción</label>
                <textarea
                  className={inputClass}
                  rows={3}
                  name="descripcion"
                  value={educationData.descripcion}
                  onChange={handleChange}
                  placeholder="Ejemplo: Estudiante destacado con intereses en inteligencia artificial..."
                  maxLength="2000"
                />
              </div>
            </div>
            {isOwner && (
              <DialogFooter>
                {educationData.id_education && (
                  <Button variant="destructive" onClick={() => handleDelete(educationData.id_education)}>
                    Eliminar educación
                  </Button>
                )}
                <Button onClick={handleSave}>Guardar</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default UserEducation;
